import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, X, Package, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import useApi from "../utils/useApi";
import apis from "../utils/apis";
import { RETURN_TYPE } from "../utils/constants";
import AddressSelectorModal from "./AddressSelectorModal";
import useAddress from "../hooks/useAddress";
import useImageUpload from "../hooks/useImageUpload";

const ReturnOrderModal = ({ 
  isOpen, 
  onClose, 
  orderId,
  orderProducts = [],
  onReturnSuccess
}) => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [returnType, setReturnType] = useState(RETURN_TYPE.REFUND);
  const [returnImages, setReturnImages] = useState([]);
  const [pickupAddress, setPickupAddress] = useState(null); // Store address ID
  const [isAddressListModalOpen, setIsAddressListModalOpen] = useState(false);
  
  const { callApi: submitReturn, isLoading: submittingReturn } = useApi();
  const { uploadImage, isUploading } = useImageUpload();
  
  const {
    addresses,
    userDefaultAddress,
    handleAddressComplete,
    fetchAddresses,
  } = useAddress({ loadDefault: true });

  const returnReasons = [
    { key: "expired", label: t("orders.returnModal.reasons.expired") },
    { key: "damaged", label: t("orders.returnModal.reasons.damaged") },
    { key: "wrongItem", label: t("orders.returnModal.reasons.wrongItem") },
    { key: "poorQuality", label: t("orders.returnModal.reasons.poorQuality") },
    { key: "missingItems", label: t("orders.returnModal.reasons.missingItems") },
    { key: "packagingDamaged", label: t("orders.returnModal.reasons.packagingDamaged") },
    { key: "noMatch", label: t("orders.returnModal.reasons.noMatch") },
    { key: "differentBrand", label: t("orders.returnModal.reasons.differentBrand") },
    { key: "other", label: t("orders.returnModal.reasons.other") }
  ];

  useEffect(() => {
    if (isOpen) {
      // Set default address as pickup address only on initial open
      if (!pickupAddress) {
        setPickupAddress(userDefaultAddress?._id || null);
      }
      setReturnReason("");
      setReturnImages([]);
      // Auto-select first product if there's only one
      if (orderProducts && orderProducts.length === 1) {
        setSelectedProduct(orderProducts[0].product._id);
      } else {
        setSelectedProduct(null);
      }
    }
  }, [isOpen, orderProducts]);

  // Separate effect to set pickup address when default address loads
  useEffect(() => {
    if (isOpen && userDefaultAddress?._id && !pickupAddress) {
      setPickupAddress(userDefaultAddress._id);
    }
  }, [userDefaultAddress, isOpen]);

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    const maxFiles = 5;
    
    if (returnImages.length + files.length > maxFiles) {
      toast.error(t("orders.returnModal.maxImagesError") || `You can only upload up to ${maxFiles} images`);
      return;
    }

    // Upload images to S3 immediately
    try {
      // Process uploads sequentially to show loading state properly
      for (const file of files) {
        const imageUrl = await uploadImage(file);
        const newImage = {
          file,
          url: imageUrl, // Use S3 URL instead of local blob URL
          localUrl: URL.createObjectURL(file), // Keep local URL for preview
          id: Math.random().toString(36).substr(2, 9),
          uploaded: true
        };
        setReturnImages(prev => [...prev, newImage]);
      }
      
      toast.success(`${files.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images. Please try again.");
    }
  };

  const removeImage = (imageId) => {
    setReturnImages(prev => {
      const updatedImages = prev.filter(img => img.id !== imageId);
      // Clean up local object URL
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove?.localUrl) {
        URL.revokeObjectURL(imageToRemove.localUrl);
      }
      return updatedImages;
    });
  };

  const handleSubmit = async () => {
    if (!selectedProduct) {
      toast.error(t("orders.returnModal.selectProductError"));
      return;
    }
    
    if (!returnReason) {
      toast.error(t("orders.returnModal.selectReasonError"));
      return;
    }

    if (!pickupAddress) {
      toast.error(t("orders.returnModal.selectAddressError"));
      return;
    }

    try {
      // Images are already uploaded, just get the URLs
      const imageUrls = returnImages.map(img => img.url);

      // Send the return request with image URLs
      await submitReturn({
        api: apis.createReturn,
        body: {
          orderId,
          productId: selectedProduct,
          reason: returnReason,
          type: returnType,
          pickupAddress,
          images: imageUrls
        },
      });

      toast.success(t("orders.returnModal.submitSuccess"));
      // Call the success callback to refresh order data
      if (onReturnSuccess) {
        onReturnSuccess();
      } else {
        handleClose();
      }
    } catch (error) {
      console.error("Error submitting return:", error);
      toast.error(t("orders.returnModal.submitError"));
    }
  };

  const handleClose = () => {
    returnImages.forEach(image => {
      if (image.localUrl) {
        URL.revokeObjectURL(image.localUrl);
      }
    });
    setSelectedProduct("");
    setReturnReason("");
    setReturnType(RETURN_TYPE.REFUND);
    setReturnImages([]);
    setPickupAddress("");
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            className="bg-white dark:bg-dark-background rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto section"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {t("orders.returnModal.title")}
                </h3>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Return Type Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("orders.returnModal.returnTypeLabel")}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setReturnType(RETURN_TYPE.REFUND)}
                    className={`p-2 rounded-lg border-2 transition-all ${
                      returnType === RETURN_TYPE.REFUND
                        ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                        : "border-gray-200 dark:border-gray-600 hover:border-orange-300"
                    }`}
                  >
                    <Package className={`w-5 h-5 mx-auto mb-1 ${
                      returnType === RETURN_TYPE.REFUND ? "text-orange-600" : "text-gray-400"
                    }`} />
                    <p className={`text-xs font-medium ${
                      returnType === RETURN_TYPE.REFUND 
                        ? "text-orange-600 dark:text-orange-400" 
                        : "text-gray-600 dark:text-gray-400"
                    }`}>
                      {t("orders.returnModal.refund")}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReturnType(RETURN_TYPE.REPLACEMENT)}
                    className={`p-2 rounded-lg border-2 transition-all ${
                      returnType === RETURN_TYPE.REPLACEMENT
                        ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                        : "border-gray-200 dark:border-gray-600 hover:border-orange-300"
                    }`}
                  >
                    <RefreshCw className={`w-5 h-5 mx-auto mb-1 ${
                      returnType === RETURN_TYPE.REPLACEMENT ? "text-orange-600" : "text-gray-400"
                    }`} />
                    <p className={`text-xs font-medium ${
                      returnType === RETURN_TYPE.REPLACEMENT 
                        ? "text-orange-600 dark:text-orange-400" 
                        : "text-gray-600 dark:text-gray-400"
                    }`}>
                      {t("orders.returnModal.replacement")}
                    </p>
                  </button>
                </div>
              </div>

              {/* Product Selection */}
              {orderProducts && orderProducts.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("orders.returnModal.selectProductLabel")}
                  </label>
                  <select
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-dark-background text-gray-900 dark:text-gray-100 section"
                    required
                  >
                    <option value="">{t("orders.returnModal.selectProductPlaceholder")}</option>
                    {orderProducts.map((item) => (
                      <option key={item._id} value={item.product._id}>
                        {item.product.name} (Qty: {item.quantity})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Return Reason */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("orders.returnModal.reasonLabel")}
                </label>
                <select
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-dark-background text-gray-900 dark:text-gray-100 section"
                  required
                >
                  <option value="">{t("orders.returnModal.reasonPlaceholder")}</option>
                  {returnReasons.map((reason, index) => (
                    <option key={index} value={reason.key}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pickup Address Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("orders.returnModal.pickupAddressLabel")}
                </label>
                {pickupAddress ? (
                  <div className="border border-primary-100 bg-primary-100/10 dark:bg-primary-100/20 rounded-lg p-3">
                    {(() => {
                      // Try to find in addresses array first, fallback to userDefaultAddress if addresses not loaded yet
                      const addressToShow = addresses?.find(addr => addr._id === pickupAddress) 
                        || (pickupAddress === userDefaultAddress?._id ? userDefaultAddress : null);
                      
                      return addressToShow ? (
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-0.5">
                            {addressToShow.name}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5">
                            {addressToShow.address}, {addressToShow.city}, {addressToShow.state} - {addressToShow.pin_code}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Phone: {addressToShow.mobile}
                          </p>
                          <button
                            onClick={() => setIsAddressListModalOpen(true)}
                            className="mt-1.5 text-xs text-primary-100 hover:text-primary-600 font-medium"
                          >
                            {t("cart.deliveryAddress.change")}
                          </button>
                        </div>
                      ) : null;
                    })()}
                  </div>
                ) : (
                  <button
                    onClick={() => setIsAddressListModalOpen(true)}
                    className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:border-primary-100 dark:hover:border-primary-100 transition-colors text-gray-600 dark:text-gray-400 hover:text-primary-100 font-medium text-sm"
                  >
                    + {t("orders.returnModal.selectAddressButton")}
                  </button>
                )}
              </div>

              {/* Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {t("orders.returnModal.imagesLabel")}
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-2">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="return-images"
                  />
                  <label
                    htmlFor="return-images"
                    className={`cursor-pointer flex items-center justify-center gap-2 ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    {isUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {t("orders.returnModal.uploadingImages")}
                        </span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 text-gray-400" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {t("orders.returnModal.imagesHint")}
                        </span>
                      </>
                    )}
                  </label>
                </div>
                
                {returnImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {returnImages.map((image) => (
                      <div key={image.id} className="relative size-32">
                        <img
                          src={image.localUrl || image.url}
                          alt="Return"
                          className="size-full object-contain rounded-md"
                        />
                        <button
                          onClick={() => removeImage(image.id)}
                          className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={handleClose}
                  disabled={submittingReturn || isUploading}
                  className="flex-1 px-3 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 rounded-md font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 text-sm"
                >
                  {t("orders.returnModal.cancel")}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!returnReason || !selectedProduct || !pickupAddress || submittingReturn || isUploading}
                  className="flex-1 px-3 py-2 bg-orange-600 text-white rounded-md font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
                >
                  {submittingReturn ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white mr-1.5"></div>
                      <span className="text-xs">{t("orders.returnModal.submitting")}</span>
                    </div>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      
      {/* Address List Modal */}
      <AddressSelectorModal
        isOpen={isAddressListModalOpen}
        onClose={() => setIsAddressListModalOpen(false)}
        title={t("orders.returnModal.selectPickupAddress")}
        setAsDefault={false}
        selectedAddressId={pickupAddress}
        onAddressSelected={setPickupAddress}
        addresses={addresses}
        onAddressComplete={async (addressData) => {
          await handleAddressComplete(addressData);
          await fetchAddresses();
        }}
      />
    </AnimatePresence>
  );
};

export default ReturnOrderModal;
